import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  GetLocationsResponse,
  GetPatientResponse,
  LocationProvider,
  Patient,
  SearchPatientsRequest,
  SearchPatientsResponse,
  OrgAlertHistoryRequest,
  OrgAlertHistoryResponse,
  OrgActiveAlertsResponse,
  UpdateLocationRequest,
  CreateLocationRequest,
  UpdatePatientRequest,
} from './patient.model';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  http = inject(HttpClient);

  /**
   * Retrieves a patient's details from the server.
   *
   * @param patientId - The unique identifier of the patient to retrieve.
   * @returns An Observable that emits the patient's details.
   * @throws An error if the patient is not found.
   */
  getPatient(patientId: string): Observable<Patient> {
    return this.http
      .get<GetPatientResponse>(
        `${environment.urls.standaloneApi}/v1/organization/patients/${patientId}`,
      )
      .pipe(
        map((response) => {
          if (!response.result) {
            throw new Error('Patient not found');
          }
          return response.result;
        }),
      );
  }

  /**
   * Searches for patients based on the provided criteria.
   *
   * @param args - The search criteria for finding patients.
   * @returns An Observable that emits the search results.
   */
  getPatients(args: SearchPatientsRequest): Observable<SearchPatientsResponse> {
    let params = new HttpParams();
    Object.keys(args).forEach((key) => {
      const value = args[key as keyof SearchPatientsRequest];
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    return this.http.get<SearchPatientsResponse>(
      `${environment.urls.standaloneApi}/v1/organization/patients`,
      { params },
    );
  }

  createPatient(params: Patient): Observable<unknown> {
    return this.http.post(`${environment.urls.standaloneApi}/v1/organization/patients`, {
      ...params,
    });
  }

  updatePatient(patientId: string, request: UpdatePatientRequest): Observable<Patient> {
    return this.http.put<Patient>(
      `${environment.urls.standaloneApi}/v1/organization/patients/${encodeURIComponent(patientId)}`,
      request,
    );
  }

  /**
   * Retrieves the locations associated with the organization.
   *
   * @returns An Observable that emits the locations.
   */
  getLocations(): Observable<GetLocationsResponse> {
    return this.http.get<GetLocationsResponse>(
      `${environment.urls.standaloneApi}/v1/organization/locations`,
    );
  }

  createLocation(request: CreateLocationRequest): Observable<LocationProvider> {
    return this.http.post<LocationProvider>(
      `${environment.urls.standaloneApi}/v1/organization/locations`,
      request,
    );
  }

  updateLocation(locationId: string, request: UpdateLocationRequest): Observable<LocationProvider> {
    return this.http.put<LocationProvider>(
      `${environment.urls.standaloneApi}/v1/organization/locations/${encodeURIComponent(locationId)}`,
      request,
    );
  }

  deleteLocation(locationId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.urls.standaloneApi}/v1/organization/locations/${encodeURIComponent(locationId)}`,
    );
  }

  getOrgAlertHistory(args: OrgAlertHistoryRequest = {}): Observable<OrgAlertHistoryResponse> {
    let params = new HttpParams();
    if (args.pageSize) {
      params = params.append('pageSize', args.pageSize);
    }
    if (args.nextPage) {
      params = params.append('nextPage', args.nextPage);
    }
    if (args.status) {
      params = params.append('status', args.status);
    }
    if (args.campaignId && args.campaignId !== 'all') {
      params = params.append('campaignId', args.campaignId);
    }
    if (args.dateFrom != null) {
      params = params.append('dateFrom', args.dateFrom);
    }
    if (args.dateTo != null) {
      params = params.append('dateTo', args.dateTo);
    }
    if (args.search?.trim()) {
      params = params.append('search', args.search.trim());
    }
    if (args.callSequenceId?.trim()) {
      params = params.append('callSequenceId', args.callSequenceId.trim());
    }
    return this.http.get<OrgAlertHistoryResponse>(
      `${environment.urls.standaloneApi}/v1/organization/alerts`,
      { params },
    );
  }

  getActiveAlertPatientIds(): Observable<OrgActiveAlertsResponse> {
    return this.http.get<OrgActiveAlertsResponse>(
      `${environment.urls.standaloneApi}/v1/organization/alerts`,
      { params: new HttpParams().set('status', 'active') },
    );
  }
}
